import React, { useState } from 'react';
import api from '../utils/api';

const UserManagementPanel = () => {
  const [createForm, setCreateForm] = useState({
    email: '',
    full_name: '',
    role: 'team_member',
    team_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCreateForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);

    try {
      // Validate form
      if (!createForm.email || !createForm.full_name || !createForm.role) {
        setMessage({ type: 'error', text: 'Email, full name, and role are required' });
        setLoading(false);
        return;
      }

      // Prepare payload
      const payload = {
        email: createForm.email,
        full_name: createForm.full_name,
        role: createForm.role
      };

      // Add team_id if provided
      if (createForm.team_id) {
        payload.team_id = parseInt(createForm.team_id);
      }

      // Call API to create user
      await api.post('/admin/users/create', payload);

      // Show success modal with credentials
      setSuccessModal({
        email: createForm.email,
        full_name: createForm.full_name,
        password: 'password'
      });

      // Reset form
      setCreateForm({
        email: '',
        full_name: '',
        role: 'team_member',
        team_id: ''
      });

      setMessage({ type: 'success', text: 'User created successfully!' });
    } catch (error) {
      console.error('Error creating user:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to create user'
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: 'Copied to clipboard!' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Create New User</h2>
        <p className="text-gray-600 mb-6">
          Create a new user account with default password "password". User must change it on first login.
        </p>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg border ${
            message.type === 'error'
              ? 'bg-red-100 border-red-400 text-red-700'
              : 'bg-green-100 border-green-400 text-green-700'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={createForm.email}
            onChange={handleInputChange}
            placeholder="user@example.com"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Full Name */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="full_name"
            value={createForm.full_name}
            onChange={handleInputChange}
            placeholder="John Doe"
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Role */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Role <span className="text-red-500">*</span>
          </label>
          <select
            name="role"
            value={createForm.role}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
          >
            <option value="team_member">Team Member</option>
            <option value="team_leader">Team Leader</option>
          </select>
        </div>

        {/* Team (Optional) */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Team <span className="text-gray-500">(Optional)</span>
          </label>
          <input
            type="number"
            name="team_id"
            value={createForm.team_id}
            onChange={handleInputChange}
            placeholder="Leave empty if user has no team yet"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Users can be assigned to teams later</p>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-2 px-4 rounded-lg font-semibold text-white transition ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? 'Creating User...' : 'Create User'}
          </button>
        </div>
      </form>

      {/* Success Modal */}
      {successModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="text-2xl font-bold text-gray-800">User Created Successfully!</h3>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 space-y-4 mb-6">
              <div>
                <label className="text-sm font-semibold text-gray-600">User Name</label>
                <div className="mt-2 p-3 bg-white border border-gray-200 rounded flex justify-between items-center">
                  <span className="font-mono text-gray-800">{successModal.full_name}</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600">Email</label>
                <div className="mt-2 p-3 bg-white border border-gray-200 rounded flex justify-between items-center">
                  <span className="font-mono text-gray-800">{successModal.email}</span>
                  <button
                    onClick={() => copyToClipboard(successModal.email)}
                    className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600">Default Password</label>
                <div className="mt-2 p-3 bg-white border border-gray-200 rounded flex justify-between items-center">
                  <span className="font-mono text-gray-800">{successModal.password}</span>
                  <button
                    onClick={() => copyToClipboard(successModal.password)}
                    className="text-blue-600 hover:text-blue-800 font-semibold text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>📝 Note:</strong> User must change their password on first login.
              </p>
            </div>

            <button
              onClick={() => setSuccessModal(null)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPanel;
